import { CircularProgress, Box, useMediaQuery, useTheme, Drawer, IconButton, Typography } from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import { DisplayFlexColumn } from 'src/shared/styled/sharedPrimitives'
import { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react'
// PERFORMANCE: LiveSessionPageComponents imports are deferred - only used for device selection
// which is conditionally rendered based on availableCameras/availableMicrophones
import {
  AudioInputToggleComponent,
  SelectForMediaDevices,
  VideoInputToggleComponent,
} from 'src/consumer/components/LiveSessionPage/LiveSessionPageComponents'
import { LSButton } from 'src/consumer/components/LiveSessionPage/LiveSessionPageStyles'
import { JoinSessionRelatedSelectorButtons } from 'src/consumer/components/PublicLiveSessionPage/components/styledComponents'
import { APP_CONSTANTS, getEventTypeObject, BOOKING_STATUSES, BOOKING_PAYMENT_STATUSES } from 'src/consumer/constants'
import { openInNewTab } from 'src/consumer/helpers/utilFunctions'
import { mediaURL } from 'src/consumer/services'
import {
  setSelectedCameraInLocalStorage,
  setSelectedMicrophoneInLocalStorage,
  setSelectedSpeakerInLocalStorage,
} from 'src/consumer/services/localstorageServices'
// PERFORMANCE OPTIMIZATION: Heavy prescription/export imports are now DYNAMIC
// These are only used when showPdfRelatedActions=true (mentor/clinic flows only)
// For public session pages (/v/*), these are NEVER used, saving ~100KB+
// The functions below load them on-demand when actually needed
import { sleep } from 'src/mentor/helpers/utilityFunctions'
import dayjs from 'dayjs'

// LAZY LOADED: WaitingEngagement for countdown screen entertainment
const WaitingEngagement = lazy(() => 
  import('./WaitingEngagement/WaitingEngagement')
)

// LAZY LOADED: ExportTypeSelector is only used for PDF-related actions (mentor/clinic flows)
const ExportTypeSelector = lazy(() => 
  import('src/clinic/views/clinicBrandPayoutReports/components/ExportTypeSelector')
)

export default function JoinSessionRelatedButtons({
  microphoneError,
  cameraError,
  userHasAllowedAudio,
  userHasAllowedVideo,
  setUserHasAllowedAudio,
  setUserHasAllowedVideo,
  setGiveMicrophonePermissionDialogOpen,
  setGiveCameraPermissionDialogOpen,
  tokenLoading,
  handleJoinRoom,
  availableCameras,
  availableMicrophones,
  selectedCamera,
  selectedMicrophone,
  setSelectedCamera,
  setSelectedMicrophone,
  hideAllSesionsButton = false,
  handleSeeAllSessions,
  showRejoin = false,
  isVideoCallScreen = false,
  showTermsText = false,
  isLinkExpired = false,
  linkExpiryText = 'Link Expired',
  waitUntilInMs = null,
  bookedEventData,
  onlyShowSelectors = false,
  availableSpeakers,
  selectedSpeaker,
  setSelectedSpeaker,
  isViewOnly = false,
  showPdfRelatedActions = false,
  auxiliaryData,
  hideDeviceSelection = false,
  engagementConfig = null, // Waiting screen engagement config (music tracks, games)
  sessionSlug = null, // Public session slug for music URL fetching
  clientUserLoginHint = null, // Masked login hint for music access (e.g., "j***@example.com")
  clientUserLoginMethod = null, // Login method: 'email', 'mobile', 'google', 'facebook', 'apple'
}) {
  const eventTypeData = getEventTypeObject(bookedEventData?.eventType)
  
  // Check if session is blocked (slot held but payment pending)
  const isSessionBlocked = 
    bookedEventData?.status === BOOKING_STATUSES.BOOKING_INITIATED &&
    bookedEventData?.paymentStatus === BOOKING_PAYMENT_STATUSES.PENDING
  const [isSendingPdf, setIsSendingPdf] = useState(false)
  const [timeRemaining, setTimeRemaining] = useState(null)
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [drawerContent, setDrawerContent] = useState(null) // 'privacy' or 'terms'
  const theme = useTheme()
  
  // Store handleJoinRoom in a ref to preserve it across re-renders
  // Update ref synchronously (not in useEffect) to avoid infinite loops
  const handleJoinRoomRef = useRef(handleJoinRoom)
  handleJoinRoomRef.current = handleJoinRoom
  
  // Stable click handler that always uses the latest handleJoinRoom
  const stableHandleJoinRoom = useCallback((e) => {
    e.preventDefault()
    e.stopPropagation()
    // Debug logging removed to prevent continuous logging loops
    
    if (!handleJoinRoomRef.current) {
      return
    }
    
    if (typeof handleJoinRoomRef.current !== 'function') {
      return
    }
    
    if (tokenLoading) {
      return
    }
    
    try {
      const result = handleJoinRoomRef.current()
      if (result && typeof result.then === 'function') {
        result.catch((error) => {
          console.error('Error in handleJoinRoom promise:', error)
        })
      }
    } catch (error) {
      console.error('Error calling handleJoinRoom:', error)
    }
  }, [tokenLoading])
  
  // Removed two-column layout forcing - always use single column
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'))

  // Countdown timer for link availability
  useEffect(() => {
    if (!waitUntilInMs || !isLinkExpired) {
      setTimeRemaining(null)
      return
    }

    const updateCountdown = () => {
      const now = Date.now()
      const remaining = waitUntilInMs - now

      if (remaining <= 0) {
        setTimeRemaining(0)
        // Refresh the page when countdown reaches zero
        window.location.reload()
        return
      }

      setTimeRemaining(remaining)
    }

    // Update immediately
    updateCountdown()

    // Update every second
    const interval = setInterval(updateCountdown, 1000)

    return () => clearInterval(interval)
  }, [waitUntilInMs, isLinkExpired])

  // Format time remaining as HH:MM:SS
  const formatTimeRemaining = (ms) => {
    if (ms === null || ms <= 0) return '00:00:00'

    const totalSeconds = Math.floor(ms / 1000)
    const hours = Math.floor(totalSeconds / 3600)
    const minutes = Math.floor((totalSeconds % 3600) / 60)
    const seconds = totalSeconds % 60

    return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`
  }
  async function handleDownloadPDF({ sendImmediately = false }) {
    setIsSendingPdf(true)
    await sleep(800)
    
    // DYNAMIC IMPORT: Prescription helpers are only loaded when PDF download is requested
    // This saves ~50KB+ from the initial bundle for public session pages
    const { generatePrescriptionPDF, downloadPDFFromURL } = await import(
      'src/mentor/components/PrescriptionMakingPage/prescriptionHelperFunctions'
    )
    
    const data = await generatePrescriptionPDF({
      bookedEventId: bookedEventData?.id,
      sendImmediately,
    })
    if (!!data) {
      const pdfUrl = `${mediaURL}${data?.pdfMediaId}`
      const filename = `prescription_${
        auxiliaryData?.metaPrescription?.patientPrefill?.patientNumber
      }_${dayjs().format('YYYY-MM-DD HH:mm:ss')}.pdf`
      await downloadPDFFromURL(pdfUrl, filename)
    }
    setIsSendingPdf(false)
  }

  // Combined Consent and Join Section - Privacy Policy + Button together
  const ConsentAndJoinSection = () => {
    const handlePrivacyClick = (e) => {
      e.preventDefault()
      e.stopPropagation()
      // Debug logging removed to prevent continuous logging loops
      setDrawerContent('privacy')
      setDrawerOpen(true)
    }

    const handleTermsClick = (e) => {
      e.preventDefault()
      e.stopPropagation()
      // Debug logging removed to prevent continuous logging loops
      setDrawerContent('terms')
      setDrawerOpen(true)
    }

    return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: { xs: '20px', md: '24px' },
        alignItems: 'center',
        width: '100%',
        maxWidth: { xs: '100%', sm: '600px', md: '700px' },
        marginLeft: 'auto',
        marginRight: 'auto',
      }}
    >
      {/* Privacy Policy Text - Using span to avoid p tag animations */}
      <Box
        sx={{
          padding: { xs: '16px', md: '18px' },
          width: '100%',
          backgroundColor: '#ffffff',
          borderRadius: { xs: '12px', md: '16px' },
          border: '1px solid #e5e7eb',
          position: 'relative',
          zIndex: 1000,
          animation: 'none !important',
          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
          pointerEvents: 'auto',
          '& *': {
            animation: 'none !important',
            transition: 'none !important',
          },
        }}
      >
        <Box
          component="span"
          sx={{
            margin: 0,
            padding: 0,
            fontSize: '14px',
            color: '#111827',
            lineHeight: 1.6,
            textAlign: 'center',
            fontWeight: 400,
            opacity: 1,
            visibility: 'visible',
            display: 'block',
            animation: 'none !important',
            transition: 'none !important',
          }}
        >
          By joining this session, you agree to our{' '}
          <Box
            component="span"
            onClick={handlePrivacyClick}
            sx={{
              color: '#6366f1',
              textDecoration: 'underline',
              fontWeight: 500,
              cursor: 'pointer',
              pointerEvents: 'auto',
              position: 'relative',
              zIndex: 1001,
              animation: 'none !important',
              '&:hover': {
                color: '#8b5cf6',
              },
            }}
          >
            Privacy Policy
          </Box>{' '}
          and{' '}
          <Box
            component="span"
            onClick={handleTermsClick}
            sx={{
              color: '#6366f1',
              textDecoration: 'underline',
              fontWeight: 500,
              cursor: 'pointer',
              pointerEvents: 'auto',
              position: 'relative',
              zIndex: 1001,
              animation: 'none !important',
              '&:hover': {
                color: '#8b5cf6',
              },
            }}
          >
            Terms and Conditions
          </Box>
        </Box>
      </Box>

      {/* Join Button - Clean modern design */}
      <Box
        component="button"
        onClick={(e) => {
          // Debug logging removed to prevent continuous logging loops
          stableHandleJoinRoom(e)
        }}
        disabled={tokenLoading}
        sx={{
          background: tokenLoading
            ? '#9ca3af'
            : 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
          border: 'none',
          borderRadius: { xs: '12px', md: '16px' },
          padding: { xs: '16px 32px', md: '18px 40px' },
          minWidth: { xs: '100%', sm: '240px' },
          width: { xs: '100%', sm: 'auto' },
          maxWidth: { xs: '100%', sm: '360px', md: '400px' },
          color: '#ffffff',
          fontSize: { xs: '16px', md: '17px' },
          fontWeight: 600,
          cursor: tokenLoading ? 'not-allowed' : 'pointer',
          boxShadow: tokenLoading
            ? '0 2px 4px rgba(0, 0, 0, 0.1)'
            : '0 4px 12px rgba(139, 92, 246, 0.3), 0 2px 6px rgba(99, 102, 241, 0.2)',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '8px',
          touchAction: 'manipulation',
          alignSelf: { xs: 'stretch', sm: 'center' },
          '&:hover:not(:disabled)': {
            transform: { xs: 'none', md: 'translateY(-2px)' },
            boxShadow: tokenLoading
              ? '0 2px 4px rgba(0, 0, 0, 0.1)'
              : '0 6px 16px rgba(139, 92, 246, 0.4), 0 3px 8px rgba(99, 102, 241, 0.3)',
          },
          '&:active:not(:disabled)': {
            transform: 'scale(0.98)',
          },
        }}
      >
        {tokenLoading ? (
          <>
            <CircularProgress
              sx={{ height: '22px !important', width: '22px !important', color: 'white' }}
            />
            <span>Preparing...</span>
          </>
        ) : showRejoin ? (
          `Rejoin ${eventTypeData?.altTitle || eventTypeData?.title || 'Session'}`
        ) : (
          `Join ${eventTypeData?.altTitle || eventTypeData?.title || 'Session'}`
        )}
      </Box>

      {/* Secondary Actions */}
      {(showRejoin && !hideAllSesionsButton) || (showRejoin && showPdfRelatedActions) ? (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: { xs: '10px', md: '12px' },
            width: '100%',
            alignItems: 'center',
          }}
        >
      {showRejoin && !hideAllSesionsButton && (
        <Box
          component="button"
          onClick={handleSeeAllSessions}
          sx={{
            background: '#ffffff',
            border: '1px solid #d1d5db',
            borderRadius: { xs: '12px', md: '16px' },
            padding: { xs: '14px 28px', md: '16px 32px' },
            minWidth: { xs: '100%', sm: '200px' },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '100%', sm: '300px' },
            color: '#4b5563',
            fontSize: { xs: '14px', md: '15px' },
            fontWeight: 500,
            cursor: 'pointer',
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
            transition: 'all 0.2s ease',
            '&:hover': {
              background: '#f9fafb',
              borderColor: '#9ca3af',
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
            },
          }}
        >
          See All Sessions
        </Box>
      )}
      {/* LAZY LOADED: ExportTypeSelector is wrapped in Suspense - only loads for mentor/clinic flows */}
      {showRejoin && showPdfRelatedActions && (
        <Suspense fallback={
          <Box
            component="button"
            disabled
            sx={{
              background: '#f3f4f6',
              border: '1px solid #d1d5db',
              borderRadius: { xs: '12px', md: '16px' },
              padding: { xs: '14px 28px', md: '16px 32px' },
              minWidth: { xs: '100%', sm: '200px' },
              width: { xs: '100%', sm: 'auto' },
              maxWidth: { xs: '100%', sm: '300px' },
              color: '#9ca3af',
              fontSize: { xs: '14px', md: '15px' },
              fontWeight: 500,
            }}
          >
            Loading...
          </Box>
        }>
          <ExportTypeSelector
            emailLabel="Send PDF to patient"
            mainButton={({ handleClick, isOpen }) => {
              return (
                <Box
                  component="button"
                  onClick={handleClick}
                  disabled={isSendingPdf}
                  sx={{
                    background: '#ffffff',
                    border: '1px solid #d1d5db',
                    borderRadius: { xs: '12px', md: '16px' },
                    padding: { xs: '14px 28px', md: '16px 32px' },
                    minWidth: { xs: '100%', sm: '200px' },
                    width: { xs: '100%', sm: 'auto' },
                    maxWidth: { xs: '100%', sm: '300px' },
                    color: '#4b5563',
                    fontSize: { xs: '14px', md: '15px' },
                    fontWeight: 500,
                    cursor: isSendingPdf ? 'not-allowed' : 'pointer',
                    boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    '&:hover:not(:disabled)': {
                      background: '#f9fafb',
                      borderColor: '#9ca3af',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
                    },
                  }}
                >
                  Download PDF
                  {isSendingPdf && (
                    <CircularProgress
                      sx={{
                        height: '18px !important',
                        width: '18px !important',
                        color: '#6b7280',
                      }}
                    />
                  )}
                </Box>
              )
            }}
            handleSelectExportType={handleDownloadPDF}
            hideOptionsId={['excel']} // Inlined value - EXPORT_TYPES.excel.id = 'excel'
          />
        </Suspense>
      )}
        </Box>
      ) : null}
    </Box>
    )
  }

  // Blocked session message
  const BlockedSessionContent = () => (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        gap: { xs: '16px', md: '20px' },
        padding: { xs: '24px 20px', md: '32px' },
        background: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(20px)',
        borderRadius: { xs: '16px', md: '24px' },
        border: '1px solid rgba(251, 191, 36, 0.3)',
        boxShadow: '0 8px 32px rgba(251, 191, 36, 0.15)',
        maxWidth: '500px',
        width: '100%',
      }}
    >
      <Box
        sx={{
          width: '64px',
          height: '64px',
          borderRadius: '50%',
          background: 'rgba(251, 191, 36, 0.15)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: '32px',
        }}
      >
        ⏳
      </Box>
      <Box
        sx={{
          fontSize: { xs: '18px', md: '20px' },
          fontWeight: 700,
          color: '#92400e',
          textAlign: 'center',
        }}
      >
        Session Not Yet Confirmed
      </Box>
      <Box
        sx={{
          fontSize: '14px',
          color: '#78350f',
          textAlign: 'center',
          lineHeight: 1.6,
          maxWidth: '400px',
        }}
      >
        This session slot has been held but is awaiting payment confirmation. 
        The session will become available once payment is completed.
      </Box>
      <Box
        sx={{
          fontSize: '13px',
          color: '#92400e',
          textAlign: 'center',
          padding: '12px 20px',
          background: 'rgba(251, 191, 36, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(251, 191, 36, 0.2)',
        }}
      >
        Please contact support if you believe this is an error.
      </Box>
    </Box>
  )

  const mainContent = isSessionBlocked ? (
    <BlockedSessionContent />
  ) : !isLinkExpired ? (
    !isViewOnly ? (
      <>
        {!onlyShowSelectors && (
          <ConsentAndJoinSection />
        )}
        {/* Camera/Mic Controls - Only show if not hidden and device selection is needed */}
        {!hideDeviceSelection && ((isVideoCallScreen && !onlyShowSelectors) || (isVideoCallScreen && (availableCameras?.length > 0 || availableMicrophones?.length > 0 || availableSpeakers?.length > 0))) ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: { xs: '20px', md: '24px' },
              width: '100%',
              maxWidth: { xs: '100%', sm: '600px', md: '700px' },
              marginLeft: 'auto',
              marginRight: 'auto',
              marginTop: { xs: '24px', md: '28px' },
            }}
          >
            {/* Camera/Mic toggles moved to CameraPreview component - removed from here */}
          {isVideoCallScreen && (availableCameras?.length > 0 || availableMicrophones?.length > 0 || availableSpeakers?.length > 0) && (
            <Box
              sx={{
                background: '#ffffff',
                borderRadius: { xs: '16px', md: '20px' },
                padding: { xs: '20px', md: '24px' },
                border: '1px solid #e5e7eb',
                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.06), 0 1px 3px rgba(0, 0, 0, 0.04)',
                width: '100%',
              }}
            >
              <JoinSessionRelatedSelectorButtons>
                {availableCameras?.length > 0 && (
                  <DisplayFlexColumn style={{ alignItems: 'center', gap: '10px' }}>
                    <Box
                      sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#374151',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Camera
                    </Box>
                    <SelectForMediaDevices
                      value={selectedCamera}
                      handleChange={(e) => {
                        setSelectedCameraInLocalStorage(e.target.value)
                        setSelectedCamera(e.target.value)
                      }}
                      options={availableCameras}
                    />
                  </DisplayFlexColumn>
                )}
                {availableMicrophones?.length > 0 && (
                  <DisplayFlexColumn style={{ alignItems: 'center', gap: '10px' }}>
                    <Box
                      sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#374151',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Microphone
                    </Box>
                    <SelectForMediaDevices
                      value={selectedMicrophone}
                      handleChange={(e) => {
                        setSelectedMicrophoneInLocalStorage(e.target.value)
                        setSelectedMicrophone(e.target.value)
                      }}
                      options={availableMicrophones}
                    />
                  </DisplayFlexColumn>
                )}
                {availableSpeakers?.length > 0 && (
                  <DisplayFlexColumn style={{ alignItems: 'center', gap: '10px' }}>
                    <Box
                      sx={{
                        fontSize: '13px',
                        fontWeight: 600,
                        color: '#374151',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      Speaker
                    </Box>
                    <SelectForMediaDevices
                      value={selectedSpeaker}
                      handleChange={(e) => {
                        setSelectedSpeakerInLocalStorage(e.target.value)
                        setSelectedSpeaker(e.target.value)
                      }}
                      options={availableSpeakers}
                    />
                  </DisplayFlexColumn>
                )}
              </JoinSessionRelatedSelectorButtons>
            </Box>
          )}
          </Box>
        ) : null}
      </>
    ) : (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          marginTop: '24px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            background: 'rgba(255, 255, 255, 0.9)',
            backdropFilter: 'blur(12px)',
            borderRadius: '16px',
            padding: '20px',
            border: '1px solid rgba(139, 92, 246, 0.2)',
            boxShadow: '0 4px 12px rgba(139, 92, 246, 0.1)',
          }}
        >
          <Box sx={{ fontSize: '14px', fontWeight: 600, color: 'rgba(0, 0, 0, 0.8)' }}>
            <strong>Selected Camera: </strong>
            {availableCameras?.find((item) => item.deviceId === selectedCamera)?.label}
          </Box>
          <Box sx={{ fontSize: '14px', fontWeight: 600, color: 'rgba(0, 0, 0, 0.8)' }}>
            <strong>Selected Microphone: </strong>
            {availableMicrophones?.find((item) => item.deviceId === selectedMicrophone)?.label}
          </Box>
          <Box sx={{ fontSize: '14px', fontWeight: 600, color: 'rgba(0, 0, 0, 0.8)' }}>
            <strong>Selected Speaker: </strong>
            {availableSpeakers?.find((item) => item.deviceId === selectedSpeaker)?.label}
          </Box>
        </Box>
      </Box>
    )
  ) : (
    <Box
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', lg: 'row' },
        alignItems: { xs: 'center', lg: 'stretch' },
        justifyContent: 'center',
        gap: { xs: '16px', sm: '20px', lg: '32px' },
        width: '100%',
        maxWidth: { xs: '100%', sm: '500px', lg: '950px' },
        margin: '0 auto',
      }}
    >
      {/* Left side: Countdown Timer */}
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: { xs: '12px', sm: '16px', lg: '20px' },
          padding: { xs: '16px', sm: '20px', lg: '28px' },
          background: 'rgba(255, 255, 255, 0.95)',
          backdropFilter: 'blur(20px)',
          borderRadius: { xs: '12px', sm: '16px', lg: '20px' },
          border: waitUntilInMs
            ? '1px solid rgba(139, 92, 246, 0.2)'
            : '1px solid rgba(239, 68, 68, 0.2)',
          boxShadow: waitUntilInMs
            ? '0 8px 32px rgba(139, 92, 246, 0.15)'
            : '0 8px 32px rgba(239, 68, 68, 0.1)',
          flex: { xs: '0 0 auto', lg: '1 1 0%' },
          maxWidth: { xs: '100%', lg: '450px' },
          width: '100%',
        }}
      >
        <Box
          sx={{
            fontSize: { xs: '15px', sm: '17px', lg: '20px' },
            fontWeight: 600,
            color: waitUntilInMs ? 'rgba(0, 0, 0, 0.8)' : '#ef4444',
            textAlign: 'center',
          }}
        >
          {linkExpiryText}
        </Box>
        {waitUntilInMs && timeRemaining !== null && (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: { xs: '8px', sm: '10px', lg: '12px' },
              width: '100%',
            }}
          >
            {/* Show when the link becomes active */}
            <Box
              sx={{
                fontSize: { xs: '12px', sm: '14px', lg: '16px' },
                fontWeight: 600,
                color: '#374151',
                textAlign: 'center',
                display: 'flex',
                alignItems: 'center',
                gap: { xs: '4px', sm: '6px' },
                flexWrap: 'wrap',
                justifyContent: 'center',
              }}
            >
              <span>You can join at</span>
              <Box
                component="span"
                sx={{
                  color: '#8b5cf6',
                  fontWeight: 700,
                }}
              >
                {dayjs(waitUntilInMs).format('h:mm A')}
              </Box>
              <span>({dayjs(waitUntilInMs).format('MMM D')})</span>
            </Box>
            <Box
              sx={{
                fontSize: { xs: '26px', sm: '34px', md: '38px', lg: '44px' },
                fontWeight: 800,
                fontFamily: 'monospace',
                background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                backgroundClip: 'text',
                letterSpacing: { xs: '1px', sm: '2px', lg: '3px' },
                textAlign: 'center',
              }}
            >
              {formatTimeRemaining(timeRemaining)}
            </Box>
            <Box
              sx={{
                fontSize: { xs: '9px', sm: '10px', lg: '11px' },
                color: 'rgba(0, 0, 0, 0.6)',
                textAlign: 'center',
                display: 'flex',
                gap: { xs: '4px', sm: '6px' },
                alignItems: 'center',
              }}
            >
              <Box component="span">Hours</Box>
              <Box component="span">•</Box>
              <Box component="span">Minutes</Box>
              <Box component="span">•</Box>
              <Box component="span">Seconds</Box>
            </Box>
            <Box
              sx={{
                fontSize: { xs: '10px', sm: '11px', lg: '12px' },
                color: 'rgba(0, 0, 0, 0.65)',
                textAlign: 'center',
                marginTop: { xs: '2px', sm: '4px' },
                padding: { xs: '8px 12px', sm: '10px 16px' },
                background: 'rgba(139, 92, 246, 0.05)',
                borderRadius: { xs: '8px', sm: '10px' },
                border: '1px solid rgba(139, 92, 246, 0.1)',
              }}
            >
              The page will automatically refresh when the link becomes available
            </Box>
          </Box>
        )}
      </Box>

      {/* Right side: Engagement Activities (when wait > 2 mins) */}
      {waitUntilInMs && timeRemaining !== null && timeRemaining > 120000 && (
        <Box
          sx={{
            flex: { xs: '0 0 auto', lg: '1 1 0%' },
            maxWidth: { xs: '100%', lg: '450px' },
            width: '100%',
          }}
        >
          <Suspense fallback={null}>
            <WaitingEngagement 
              waitTimeMs={timeRemaining} 
              minWaitThreshold={120000}
              engagementConfig={engagementConfig}
              sessionSlug={sessionSlug}
              clientUserLoginHint={clientUserLoginHint}
              clientUserLoginMethod={clientUserLoginMethod}
            />
          </Suspense>
        </Box>
      )}
      {!waitUntilInMs && (
        <Box
          onClick={() => {
            openInNewTab(APP_CONSTANTS.THERAPY_SUPPORT_WHATSAPP_LINK)
          }}
          sx={{
            fontSize: '14px',
            color: '#8b5cf6',
            textDecoration: 'underline',
            cursor: 'pointer',
            fontWeight: 500,
            '&:hover': {
              color: '#6366f1',
            },
          }}
        >
          Contact AUMHUM Support for assistance
        </Box>
      )}
    </Box>
  );

  return (
    <>
      {mainContent}
      
      {/* Terms and Privacy Policy Drawer - Always rendered */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: '90%', md: '600px', lg: '700px' },
            maxWidth: '100%',
          },
        }}
      >
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }}
        >
          {/* Drawer Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: { xs: '16px', md: '20px 24px' },
              borderBottom: '1px solid #e5e7eb',
              background: '#ffffff',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontSize: { xs: '18px', md: '20px' },
                fontWeight: 700,
                color: '#111827',
              }}
            >
              {drawerContent === 'privacy' ? 'Privacy Policy' : 'Terms and Conditions'}
            </Typography>
            <IconButton
              onClick={() => setDrawerOpen(false)}
              sx={{
                color: '#6b7280',
                '&:hover': {
                  background: '#f3f4f6',
                },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          {/* Drawer Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              background: '#ffffff',
            }}
          >
            <iframe
              src={
                drawerContent === 'privacy'
                  ? APP_CONSTANTS.APP_PRIVACY_POLICY_URL
                  : APP_CONSTANTS.APP_TERMS_URL
              }
              style={{
                width: '100%',
                height: '100%',
                border: 'none',
                minHeight: 'calc(100vh - 80px)',
              }}
              title={drawerContent === 'privacy' ? 'Privacy Policy' : 'Terms and Conditions'}
            />
          </Box>
        </Box>
      </Drawer>
    </>
  )
}
